import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Paper, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column } from './DataTable';
import { transactionStore, PendingCancellation } from '../utils/transactionStore';
import { useToast } from './ui/ToastProvider';
type InboxItem = PendingCancellation & {
  type: 'CANCELLATION';
};
export const CheckerCancellationTable: React.FC = () => {
  const {
    showToast
  } = useToast();
  const [cancellations, setCancellations] = useState<PendingCancellation[]>([]);
  const [selectedCancellation, setSelectedCancellation] = useState<PendingCancellation | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [checkerComment, setCheckerComment] = useState('');
  useEffect(() => {
    const updateCancellations = () => {
      setCancellations(transactionStore.getPendingCancellations());
    };
    const unsubscribe = transactionStore.subscribe(updateCancellations);
    updateCancellations();
    return unsubscribe;
  }, []);
  const handleViewClick = (cancellation: PendingCancellation) => {
    setSelectedCancellation(cancellation);
    setCheckerComment('');
    setViewDialogOpen(true);
  };
  const handleAction = (action: 'APPROVE' | 'REJECT') => {
    if (!selectedCancellation || !checkerComment.trim()) return;
    if (action === 'APPROVE') {
      transactionStore.approveCancellation(selectedCancellation.id, checkerComment);
      showToast(`Cancellation for ${selectedCancellation.transactionId} approved`, 'success');
    } else {
      transactionStore.rejectCancellation(selectedCancellation.id, checkerComment);
      showToast(`Cancellation for ${selectedCancellation.transactionId} rejected`, 'error');
    }
    setViewDialogOpen(false);
  };
  const columns: Column<PendingCancellation>[] = [{
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" sx={{
      fontFamily: 'monospace',
      fontWeight: 700,
      color: '#0F172A'
    }} data-id="element-329">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} data-id="element-330">
          {row.mtoName}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      bgcolor: '#F3E8FF',
      color: '#9333EA',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-331" />
  }, {
    id: 'amount',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-332">
          {row.amountFCY}
        </Typography>
  }, {
    id: 'cancellationAction',
    label: 'Action Type',
    renderCell: row => <Chip label={row.cancellationAction.replace(/_/g, ' ')} size="small" sx={{
      bgcolor: '#FEE2E2',
      color: '#DC2626',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-333" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      let color = '#FFFFFF';
      let bgColor = '#B38600';
      if (row.status === 'APPROVED') {
        bgColor = '#0B7923';
      } else if (row.status === 'REJECTED') {
        bgColor = '#AE2A19';
      }
      return <Chip label={row.status} size="small" sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem'
      }} data-id="element-334" />;
    }
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Chip label={row.requestedBy} size="small" sx={{
      bgcolor: '#F1F5F9',
      color: '#475569',
      fontWeight: 500
    }} data-id="element-335" />
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" color="text.secondary" sx={{
      fontSize: '0.8rem'
    }} data-id="element-336">
          {new Date(row.requestedAt).toLocaleString()}
        </Typography>
  }];
  const rowActions = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: (row: PendingCancellation) => handleViewClick(row)
  }];
  return <Box data-id="element-337">
      <DataTable title="Pending Cancellations" data={cancellations} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending cancellation requests to review." data-id="element-338" />

      {/* View & Review Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-339">
        <DialogTitle data-id="element-340">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-341">
            <Icon icon="lucide:x-circle" width={24} color="#EF4444" data-id="element-342" />
            <Typography variant="h6" fontWeight={600} data-id="element-343">
              Review Cancellation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-344">
          {selectedCancellation && <Box sx={{
          mt: 1
        }} data-id="element-345">
              <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }} data-id="element-346">
                Transaction ID:{' '}
                <strong data-id="element-347">{selectedCancellation.transactionId}</strong>
              </Typography>

              {/* Transaction Details */}
              <Paper variant="outlined" sx={{
            p: 2,
            mb: 2,
            bgcolor: '#F8FAFC'
          }} data-id="element-348">
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{
              display: 'block',
              mb: 1.5,
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }} data-id="element-349">
                  Transaction Details
                </Typography>
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-350">
                  <Typography variant="caption" color="text.secondary" data-id="element-351">
                    MTO:
                  </Typography>
                  <Typography variant="body2" fontWeight={600} data-id="element-352">
                    {selectedCancellation.mtoName}
                  </Typography>
                </Box>
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-353">
                  <Typography variant="caption" color="text.secondary" data-id="element-354">
                    Payout Type:
                  </Typography>
                  <Chip label={selectedCancellation.payoutType} size="small" sx={{
                bgcolor: '#F3E8FF',
                color: '#9333EA',
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-355" />
                </Box>
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }} data-id="element-356">
                  <Typography variant="caption" color="text.secondary" data-id="element-357">
                    Amount:
                  </Typography>
                  <Typography variant="body2" fontWeight={600} data-id="element-358">
                    {selectedCancellation.amountFCY}
                  </Typography>
                </Box>
              </Paper>

              {/* Cancellation Info */}
              <Paper variant="outlined" sx={{
            p: 2,
            mb: 2,
            bgcolor: '#FEF2F2',
            borderColor: '#FCA5A5'
          }} data-id="element-359">
                <Typography variant="caption" color="error.main" fontWeight={700} sx={{
              display: 'block',
              mb: 1.5,
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }} data-id="element-360">
                  Cancellation Details
                </Typography>
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-361">
                  <Typography variant="caption" color="text.secondary" data-id="element-362">
                    Action Type:
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="error.main" data-id="element-363">
                    {selectedCancellation.cancellationAction.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }} data-id="element-364">
                  <Typography variant="caption" color="text.secondary" data-id="element-365">
                    Maker's Reason:
                  </Typography>
                  <Typography variant="body2" sx={{
                fontStyle: 'italic',
                maxWidth: '70%',
                textAlign: 'right'
              }} data-id="element-366">
                    "{selectedCancellation.reason}"
                  </Typography>
                </Box>
              </Paper>

              <Divider sx={{
            mb: 2
          }} data-id="element-367" />

              {/* Checker Comment */}
              <TextField label="Checker's Comment" placeholder="Enter your comment before accepting or rejecting..." multiline rows={3} fullWidth required value={checkerComment} onChange={e => setCheckerComment(e.target.value)} data-id="element-368" />
            </Box>}
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2,
        gap: 1
      }} data-id="element-369">
          <Button onClick={() => setViewDialogOpen(false)} sx={{
          color: '#64748B'
        }} data-id="element-370">
            Close
          </Button>
          <Button variant="contained" color="error" onClick={() => handleAction('REJECT')} disabled={!checkerComment.trim()} startIcon={<Icon icon="lucide:x-circle" width={16} data-id="element-372" />} data-id="element-371">
            Reject
          </Button>
          <Button variant="contained" color="success" onClick={() => handleAction('APPROVE')} disabled={!checkerComment.trim()} startIcon={<Icon icon="lucide:check-circle" width={16} data-id="element-374" />} data-id="element-373">
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default CheckerCancellationTable;